<script setup lang="ts">
import { useForm } from 'vee-validate';
import { onMounted, ref, shallowRef } from 'vue';

import { Option } from 'lib/components/dropdown/Dropdown.vue.d';
import { isEmptyObject } from 'lib/utils/object.util';

import { QuickFilterEmits, QuickFilterProps } from './QuickFilter.vue.d';
import Calendar from '../calendar/Calendar.vue';
import { QueryParams } from '../datatable/DataTable.vue.d';
import Dropdown from '../dropdown/Dropdown.vue';
import {
  FilterField,
  MultiSelectFilterField,
} from '../filtercontainer/FilterContainer.vue.d';
import applyFilter from '../filtercontainer/helpers/applyFilter.helper';
import Icon from '../icon/Icon.vue';
import InputRangeNumber from '../inputrangenumber/InputRangeNumber.vue';
import MultiSelect from '../multiselect/MultiSelect.vue';

const props = withDefaults(defineProps<QuickFilterProps>(), {
  tableName: 'datatable',
});

const emit = defineEmits<QuickFilterEmits>();

const { values, resetForm } = useForm();

onMounted(() => {
  visibleFields.forEach((field) => {
    if (
      (field.type == 'dropdown' || field.type == 'multiselect') &&
      field.fetchOnMount
    ) {
      getOptions(
        field.fetchOptionFn,
        field.optionField ?? field.field,
        field.params,
      );
    }
  });
});

const loading = ref<Record<string, boolean>>({});
const fieldsKey = shallowRef(0);
const filterOption = ref<Record<string, Option[]>>({});

/**
 * I dont think this variable needs to be reactive
 * In the general use case, the field's visibility is not going to change,
 * only set once before the component mounted based on route or user role
 */
const visibleFields: FilterField[] = props.fields.filter(
  (field) => field.visible !== false,
);

const clear = (): void => {
  resetForm();
  fieldsKey.value++;
  apply();
};

const apply = (): void => {
  applyFilter(values, props.tableName, emit);
};

const getOptions = async (
  fn: MultiSelectFilterField<QueryParams>['fetchOptionFn'],
  field: string,
  queryParams: QueryParams = {},
): Promise<void> => {
  try {
    loading.value[field] = true;
    const params = { [field]: true, ...queryParams };
    const optionOrResData = await fn?.(params);

    if ('data' in optionOrResData) {
      const { data } = optionOrResData;
      filterOption.value[field] = data.data[field];
    } else if (Array.isArray(optionOrResData))
      filterOption.value[field] = optionOrResData;
  } catch (error) {
    console.error(error);
  } finally {
    loading.value[field] = false;
  }
};
</script>

<template>
  <div
    :class="[
      'grid grid-cols-1 items-center',
      { 'gap-3 grid-cols-[auto,max-content]': !isEmptyObject(values) },
    ]"
    data-wv-name="quickfilter"
  >
    <div
      :class="['grid gap-3 transition-transform']"
      :style="`grid-template-columns: repeat(${fields.length}, 1fr)`"
      data-wv-section="quickfilterfields"
    >
      <template
        :key="JSON.stringify(field) + fieldsKey"
        v-for="field of visibleFields"
      >
        <InputRangeNumber
          v-if="field.type == 'rangenumber'"
          v-tooltip.top="field.tooltip"
          v-bind="field"
          :label="undefined"
          :max-field-name="field.fields?.[1] ?? `${field.field}[1]`"
          :min-field-name="field.fields?.[0] ?? `${field.field}[0]`"
          @update:model-value="apply"
          use-validator
        />

        <Dropdown
          v-else-if="field.type == 'dropdown'"
          v-bind="field"
          :field-name="field.field"
          :label="undefined"
          :loading="loading[field.field]"
          :options="filterOption[field.optionField ?? field.field]"
          @show="
            getOptions(
              field.fetchOptionFn,
              field.optionField ?? field.field,
              field.params,
            )
          "
          @update:model-value="apply"
          option-label="label"
          option-value="value"
          use-validator
        />

        <MultiSelect
          v-else-if="field.type == 'multiselect'"
          v-bind="field"
          :field-name="field.field"
          :label="undefined"
          :loading="loading[field.optionField ?? field.field]"
          :options="filterOption[field.optionField ?? field.field]"
          @show="
            getOptions(
              field.fetchOptionFn,
              field.optionField ?? field.field,
              field.params,
            )
          "
          @update:model-value="apply"
          hide-header-checkbox
          option-label="label"
          option-value="value"
          use-validator
        />

        <Calendar
          v-else-if="field.type === 'calendar'"
          v-bind="field"
          :field-name="field.field"
          :label="undefined"
          @update:model-value="apply"
          selection-mode="range"
          use-validator
        />
      </template>
    </div>

    <button
      v-show="!isEmptyObject(values)"
      @click="clear"
      class="rounded-full hover:bg-danger-300/20 p-0.5"
      data-wv-section="quickfilterclearbutton"
      type="button"
    >
      <Icon class="shrink-0 w-6 h-6" icon="close" severity="danger" />
    </button>
  </div>
</template>
